/**
 * /tools/heroic-work-audit?clientId=X — STP-038 (Should-priority).
 *
 * 4 reflection prompts on the week's deep-work practice. Persists as
 * versioned `client_documents` doc_type=`heroic_work_audit`.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  deepWorkRatio: string;
  gonePatterns: string;
  cbrConnection: string;
  oneChange: string;
}

const DEFAULT_FORM: FormState = {
  deepWorkRatio: '',
  gonePatterns: '',
  cbrConnection: '',
  oneChange: '',
};

export function HeroicWorkAuditPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'heroic-work-audit');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.deepWorkRatio === 'string') next.deepWorkRatio = p.deepWorkRatio;
          if (typeof p.gonePatterns === 'string') next.gonePatterns = p.gonePatterns;
          if (typeof p.cbrConnection === 'string') next.cbrConnection = p.cbrConnection;
          if (typeof p.oneChange === 'string') next.oneChange = p.oneChange;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const canSubmit =
    !submitting &&
    hydrated &&
    (form.deepWorkRatio.trim().length > 0 ||
      form.gonePatterns.trim().length > 0 ||
      form.cbrConnection.trim().length > 0 ||
      form.oneChange.trim().length > 0);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.heroicWorkAudit.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      ...(form.deepWorkRatio.trim() ? { deepWorkRatio: form.deepWorkRatio.trim() } : {}),
      ...(form.gonePatterns.trim() ? { gonePatterns: form.gonePatterns.trim() } : {}),
      ...(form.cbrConnection.trim() ? { cbrConnection: form.cbrConnection.trim() } : {}),
      ...(form.oneChange.trim() ? { oneChange: form.oneChange.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'heroic-work-audit',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.heroicWorkAudit.errors.generic'));
      } else {
        setSubmitError(t('tools.heroicWorkAudit.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="heroic-work-audit-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.heroicWorkAudit.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.heroicWorkAudit.errors.missingClient')}</p>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.heroicWorkAudit.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.heroicWorkAudit.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">
          {t('tools.heroicWorkAudit.description')}
        </p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        {(['deepWorkRatio', 'gonePatterns', 'cbrConnection', 'oneChange'] as const).map((key) => (
          <article key={key} className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
            <label className="block">
              <span className="form-label">{t(`tools.heroicWorkAudit.fields.${key}`)}</span>
              <p className="text-xs text-gray-1 mb-s2 italic">
                {t(`tools.heroicWorkAudit.hints.${key}`)}
              </p>
              <textarea
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={t(`tools.heroicWorkAudit.placeholders.${key}`)}
                className="input-field mt-s2 min-h-24"
                data-testid={`heroic-work-audit-${key}`}
                rows={4}
              />
            </label>
          </article>
        ))}

        {submitError && (
          <p role="alert" className="form-error" data-testid="heroic-work-audit-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="heroic-work-audit-saved-at">
            ✓ {t('tools.heroicWorkAudit.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.heroicWorkAudit.submitting') : t('tools.heroicWorkAudit.submit')}
        </button>
      </form>
    </section>
  );
}
